// Category ID → Ionicons mapping
// Replaces emoji icons from database with consistent Ionicons

import type { Ionicons } from '@expo/vector-icons';
import type { ComponentProps } from 'react';

type IoniconName = ComponentProps<typeof Ionicons>['name'];

interface CategoryIconConfig {
  icon: IoniconName;
  gradientColors: readonly [string, string];
}

// Default fallback
const DEFAULT_CONFIG: CategoryIconConfig = {
  icon: 'book-outline',
  gradientColors: ['#4F46E5', '#6366F1'], // Indigo
};

const CATEGORY_ICONS: Record<number, CategoryIconConfig> = {
  // Major categories
  1: { icon: 'book-outline', gradientColors: ['#4F46E5', '#6366F1'] },        // Old Testament / General
  2: { icon: 'map-outline', gradientColors: ['#059669', '#10B981'] },          // Geography & Places
  3: { icon: 'people-outline', gradientColors: ['#7C3AED', '#8B5CF6'] },      // Kings & Leaders
  4: { icon: 'sunny-outline', gradientColors: ['#0891B2', '#06B6D4'] },       // Life of Jesus
  5: { icon: 'flash-outline', gradientColors: ['#D97706', '#F59E0B'] },       // Miracles
  6: { icon: 'document-text-outline', gradientColors: ['#4F46E5', '#6366F1'] }, // New Testament
  7: { icon: 'chatbubbles-outline', gradientColors: ['#065F46', '#059669'] },  // Parables
  8: { icon: 'megaphone-outline', gradientColors: ['#9F1239', '#E11D48'] },    // Prophets
  9: { icon: 'musical-notes-outline', gradientColors: ['#1E40AF', '#3B82F6'] }, // Psalms & Wisdom
  10: { icon: 'flower-outline', gradientColors: ['#BE185D', '#EC4899'] },      // Women of the Bible

  // Biblical books — Old Testament
  11: { icon: 'leaf-outline', gradientColors: ['#059669', '#10B981'] },         // Genesis
  12: { icon: 'walk-outline', gradientColors: ['#92400E', '#D97706'] },         // Exodus
  13: { icon: 'ribbon-outline', gradientColors: ['#7C3AED', '#8B5CF6'] },       // Leviticus
  14: { icon: 'calculator-outline', gradientColors: ['#4338CA', '#6366F1'] },   // Numbers
  15: { icon: 'bookmark-outline', gradientColors: ['#1E40AF', '#3B82F6'] },     // Deuteronomy
  16: { icon: 'flag-outline', gradientColors: ['#059669', '#10B981'] },         // Joshua
  17: { icon: 'shield-outline', gradientColors: ['#92400E', '#D97706'] },       // Judges
  18: { icon: 'heart-outline', gradientColors: ['#BE185D', '#EC4899'] },        // Ruth
  19: { icon: 'person-outline', gradientColors: ['#4F46E5', '#6366F1'] },       // 1 Samuel
  20: { icon: 'person-outline', gradientColors: ['#4338CA', '#6366F1'] },       // 2 Samuel
  21: { icon: 'trophy-outline', gradientColors: ['#92400E', '#D97706'] },       // 1 Kings
  22: { icon: 'trophy-outline', gradientColors: ['#7C2D12', '#DC2626'] },       // 2 Kings
  23: { icon: 'list-outline', gradientColors: ['#4F46E5', '#6366F1'] },         // 1 Chronicles
  24: { icon: 'list-outline', gradientColors: ['#4338CA', '#6366F1'] },         // 2 Chronicles
  25: { icon: 'construct-outline', gradientColors: ['#059669', '#10B981'] },    // Ezra
  26: { icon: 'build-outline', gradientColors: ['#1E40AF', '#3B82F6'] },        // Nehemiah
  27: { icon: 'star-outline', gradientColors: ['#BE185D', '#EC4899'] },         // Esther
  28: { icon: 'help-circle-outline', gradientColors: ['#4F46E5', '#6366F1'] },  // Job
  29: { icon: 'musical-notes-outline', gradientColors: ['#1E40AF', '#3B82F6'] }, // Psalms
  30: { icon: 'bulb-outline', gradientColors: ['#D97706', '#F59E0B'] },         // Proverbs
  31: { icon: 'school-outline', gradientColors: ['#4338CA', '#6366F1'] },       // Ecclesiastes
  32: { icon: 'heart-outline', gradientColors: ['#BE185D', '#EC4899'] },        // Song of Solomon
  33: { icon: 'megaphone-outline', gradientColors: ['#7C2D12', '#DC2626'] },    // Isaiah
  34: { icon: 'megaphone-outline', gradientColors: ['#9F1239', '#E11D48'] },    // Jeremiah
  35: { icon: 'sad-outline', gradientColors: ['#4338CA', '#6366F1'] },          // Lamentations
  36: { icon: 'eye-outline', gradientColors: ['#7C3AED', '#8B5CF6'] },          // Ezekiel
  37: { icon: 'flame-outline', gradientColors: ['#D97706', '#F59E0B'] },        // Daniel
  38: { icon: 'heart-outline', gradientColors: ['#059669', '#10B981'] },         // Hosea
  39: { icon: 'bug-outline', gradientColors: ['#065F46', '#059669'] },           // Joel
  40: { icon: 'scale-outline', gradientColors: ['#92400E', '#D97706'] },         // Amos
  41: { icon: 'warning-outline', gradientColors: ['#7C2D12', '#DC2626'] },       // Obadiah
  42: { icon: 'boat-outline', gradientColors: ['#0891B2', '#06B6D4'] },          // Jonah
  43: { icon: 'scale-outline', gradientColors: ['#1E40AF', '#3B82F6'] },         // Micah
  44: { icon: 'thunderstorm-outline', gradientColors: ['#4338CA', '#6366F1'] },  // Nahum
  45: { icon: 'eye-outline', gradientColors: ['#7C3AED', '#8B5CF6'] },           // Habakkuk
  46: { icon: 'alert-circle-outline', gradientColors: ['#9F1239', '#E11D48'] },  // Zephaniah
  47: { icon: 'home-outline', gradientColors: ['#059669', '#10B981'] },           // Haggai
  48: { icon: 'telescope-outline', gradientColors: ['#D97706', '#F59E0B'] },     // Zechariah
  49: { icon: 'mail-outline', gradientColors: ['#4F46E5', '#6366F1'] },           // Malachi

  // Biblical books — New Testament
  50: { icon: 'book-outline', gradientColors: ['#0891B2', '#06B6D4'] },          // Matthew
  51: { icon: 'book-outline', gradientColors: ['#059669', '#10B981'] },          // Mark (using 51 for consistency)
  52: { icon: 'book-outline', gradientColors: ['#4F46E5', '#6366F1'] },          // Luke
  53: { icon: 'book-outline', gradientColors: ['#7C3AED', '#8B5CF6'] },          // John
  54: { icon: 'globe-outline', gradientColors: ['#D97706', '#F59E0B'] },         // Acts
  55: { icon: 'mail-outline', gradientColors: ['#4F46E5', '#6366F1'] },          // Romans
  // ... remaining NT books follow the same pattern
};

export function getCategoryIcon(categoryId: number): CategoryIconConfig {
  return CATEGORY_ICONS[categoryId] || DEFAULT_CONFIG;
}

export function getCategoryIoniconName(categoryId: number): IoniconName {
  return (CATEGORY_ICONS[categoryId] || DEFAULT_CONFIG).icon;
}

export function getCategoryGradientColors(categoryId: number): readonly [string, string] {
  return (CATEGORY_ICONS[categoryId] || DEFAULT_CONFIG).gradientColors;
}
